import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiCheckCircle, FiAlertTriangle, FiDollarSign } = FiIcons;

const GeneralLiability = () => {
  const coverageCategories = [
    { title: 'Bodily Injury', description: 'Coverage for injuries sustained by customers, clients, or third parties on your business premises or as a result of your business operations.', icon: FiShield },
    { title: 'Property Damage', description: 'Protection when your business operations cause damage to others\' property, including repairs or replacement costs.', icon: FiAlertTriangle },
    { title: 'Personal Injury', description: 'Coverage for claims of slander, libel, false arrest, or other personal injury claims against your business.', icon: FiCheckCircle },
    { title: 'Advertising Injury', description: 'Protection against claims of false or misleading advertising, copyright infringement, or trademark violations.', icon: FiDollarSign }
  ];

  const scenarios = [
    { title: 'Customer Slip and Fall', description: 'One of your employees leaves a wet spot on the tile floor and a customer slips and falls, injuring themselves severely.' },
    { title: 'Property Damage During Work', description: 'One of your employees is carrying a box and drops it on a customer\'s property, damaging the floor and wall.' },
    { title: 'Faulty Work Claims', description: 'Electrical work you performed was alleged to have been faulty and started a fire.' },
    { title: 'Product Liability', description: 'A product you sold allegedly causes bodily injury from its misuse.' },
    { title: 'Reputation Damage', description: 'Your company posts something in a newsletter that unintentionally harms the reputation of a competitor.' }
  ];

  const businessTypes = [
    'Interact face-to-face with customers and clients',
    'Work on or at a property owned by a third party',
    'Lease or own a premises',
    'Market via advertising or active in trade publications',
    'Are legally required or mandated by industry regulators to maintain sufficient general liability coverage'
  ];

  const californiaKeywords = [
    'Los Angeles', 'San Francisco', 'San Diego', 'Sacramento', 'San Jose',
    'Fresno', 'Long Beach', 'Oakland', 'Bakersfield', 'Anaheim',
    'Santa Ana', 'Riverside', 'Stockton', 'Irvine', 'Chula Vista'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)',
          }}
        />
        <div className="absolute inset-0 bg-green-900 bg-opacity-80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              General Liability Insurance for California Businesses
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-green-100 mb-8 max-w-3xl mx-auto"
            >
              General Liability (GL) insurance protects your business from liability arising from claims of damage or injury. Often required by landlords, financial institutions, and commercial clients.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex justify-center"
            >
              <a
                href="https://coterie.com/quote"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl"
              >
                Get My Price
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Coverage Categories Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Four Categories of General Liability Protection
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              General Liability insurance covers four categories of events for which your business could be held responsible.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coverageCategories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg text-center"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={category.icon} className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{category.title}</h3>
                <p className="text-gray-600">{category.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Real-World Scenarios */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Real-World Protection Scenarios
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              All of these scenarios would be covered by General Liability insurance. This type of insurance protects your small business from claims of damage done to a third party.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {scenarios.map((scenario, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-xl"
              >
                <SafeIcon icon={FiAlertTriangle} className="h-8 w-8 text-orange-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{scenario.title}</h3>
                <p className="text-gray-600">{scenario.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why GL is Important */}
      <section className="py-20 bg-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why General Liability Insurance is Critical
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                It's reasonable to assume that you're not in the business of purposely harming customers or destroying their property, but accidents happen. No matter how careful you are, some things just can't be prevented.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                General Liability Insurance shifts that liability to your insurer and away from you and your business. Your GL policy pays for the costs borne by the injured party and the defense of your business in the event of litigation.
              </p>
              <div className="bg-green-100 border-l-4 border-green-500 p-4 rounded-lg">
                <p className="text-green-800">
                  <strong>In essence:</strong> General Liability Insurance can keep you in business in situations when you'd be otherwise forced to close and liquidate.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white p-8 rounded-xl shadow-xl"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">GL is Especially Important For:</h3>
              <div className="space-y-4">
                {businessTypes.map((type, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{type}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <a
                  href="https://coterie.com/quote"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-green-600 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-green-700 transition-colors text-center"
                >
                  Get My Price
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Seamless and Stress-Free Process
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Spending time answering question after question about your business just to obtain the insurance you need is simply a waste of time. That's why we've made this process both seamless and stress-free.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Quick Application</h3>
              <p className="text-gray-600">Answer a few simple questions about your business</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Instant Quote</h3>
              <p className="text-gray-600">Get your quote immediately with transparent pricing</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Get Covered</h3>
              <p className="text-gray-600">Purchase monthly or annual coverage and get protected</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Keywords Section */}
      <section className="py-12 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-lg font-semibold text-gray-700 mb-6 text-center">
            General Liability Insurance Coverage Areas in California
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 text-sm text-gray-600">
            {californiaKeywords.map((city, index) => (
              <div key={index} className="text-center p-2 bg-white rounded-lg shadow-sm">
                General Liability Insurance {city}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GeneralLiability;